import { z } from "zod";
import { useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { Form } from "@/components/ui/form";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Textarea,
} from "@heroui/react";
import { useTranslation } from "react-i18next";

import { InputField } from "@/features/FormCard/Fields";
import { DownloadButton } from "@/components/DownloadButton";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import UploadPopover from "@/components/UploadPopover";
import { postAuthorized } from "@/lib/fetch";

import { useEditForm } from "@/features/Edit/hooks";
import { K2DataUploadNew } from "./new";
import { useQueryClient } from "@tanstack/react-query";
import { useQueryCacheManager } from "@/hooks/useQueyCache";

const statusMapper = (value: string, t: (key: string) => string) => {
  const map: Record<string, string> = {
    pending: t("badge.pending"),
    extracted: t("badge.pending"),
    transformed: t("badge.pending"),
    loaded: t("badge.validated"),
    computed: t("badge.validated"),
    error: t("badge.error"),
    ignored: t("badge.ignored"),
    preprocessed: t("badge.pending"),
  };
  return map[value] ?? value; // Fallback to raw value if not mapped
};

const FormSchema = z.object({
  status: z.string().optional(),
  file_name: z.string().optional(),
  provider_config_name: z.string().optional(),
  // needed for technical info card (tihs component use the query cache)
  created_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  error_messages: z.record(z.string(), z.unknown()).nullable().optional(),
});

export const ItemSchema = FormSchema.extend({
  id: z.number(),
});

export type ItemData = z.infer<typeof ItemSchema>;

export function K2TimeSeriesEditCard({
  id,
  client,
  table_name,
}: {
  id: number;
  client: string;
  table_name: string;
}) {
  const { t } = useTranslation();

  const itemKey = [client, table_name, "get", "item", id] as const;
  const { data, refresh } = useQueryCacheManager<ItemData>(itemKey);

  const queryClient = useQueryClient();
  const refreshList = async () => {
    await queryClient.invalidateQueries({
      queryKey: [client, "audits", "get"],
    });
  };

  const { form, onSubmit } = useEditForm({
    id,
    client,
    table_name,
    FormSchema,
  });

  useEffect(() => {
    if (data) form.reset(data);
  }, [data, form]);

  const [file, setFile] = useState<File | null>(null);

  const handleReload = async () => {
    try {
      await postAuthorized(
        `${import.meta.env.VITE_BACKEND_URL}/k2/k2_data_upload/reload/${id}`, // adjust endpoint if needed
        {},
      );
    } catch (err) {
      console.error("Error Reloading Data:", err);
    }
  };

  const handleFileChange = (files: File[]) => {
    setFile(files.length > 0 ? files[0] : null);
  };

  const formatErrorMessages = (v: unknown): string => {
    if (v == null) return "";

    // accept object or stringified JSON
    let obj: unknown = v;
    if (typeof v === "string") {
      const trimmed = v.trim();
      try {
        obj = JSON.parse(trimmed);
      } catch {
        if (trimmed === "{}" || trimmed === "[]") return "";
        return v;
      }
    }

    if (Array.isArray(v) && v.length === 0) return "";
    if (
      v &&
      typeof v === "object" &&
      Object.keys(v as Record<string, unknown>).length === 0
    )
      return "";

    const s = JSON.stringify(obj, null, 2);
    const lines = s.split("\n");
    if (
      lines.length >= 2 &&
      /^[\{\[]\s*$/.test(lines[0]) && // first line is "{" or "["
      /^[\}\]]\s*$/.test(lines[lines.length - 1]) // last is "}" or "]"
    ) {
      lines.shift(); // drop opening
      lines.pop(); // drop closing
      // remove one indent level for readability
      return lines
        .map((l) => l.replace(/^ {2}/, ""))
        .join("\n")
        .trimEnd();
    }
    return s;
  };

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full max-w-card">
      <div className="flex justify-center w-full">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Data Upload",
                })}{" "}
                #{id}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="status"
                    label={t(`${table_name}.columns.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: true,
                    }}
                    valueMapper={(value) => statusMapper(value, t)}
                  />
                  <InputField
                    name="provider_config_name"
                    label={t(`${table_name}.columns.provider_config_name`, {
                      ns: "tables",
                      defaultValue: "Configuration",
                    })}
                    control={form.control}
                    fieldOptions={{
                      isDisabled: true,
                    }}
                  />
                </div>
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <InputField
                      name="file_name"
                      label={t(`${table_name}.columns.file_name`, {
                        ns: "tables",
                        defaultValue: "Fichier",
                      })}
                      control={form.control}
                      fieldOptions={{
                        isDisabled: true,
                      }}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2 pb-1">
                    <UploadPopover
                      uploader={({ setIsOpen }) => (
                        <K2DataUploadNew
                          setIsOpen={setIsOpen}
                          files={file ? [file] : []}
                          setFiles={handleFileChange}
                          selectedConfig={form.watch("provider_config_name")}
                          setSelectedConfig={() => {}}
                          mode="modify"
                          fileId={id}
                          onSuccess={async () => {
                            await refresh();
                            await refreshList();
                          }}
                        />
                      )}
                      mode="modify"
                    />
                    <DownloadButton
                      href={`${import.meta.env.VITE_BACKEND_URL}/k2/k2_data_upload/download/${id}`}
                      size="sm"
                      variant="light"
                      color="primary"
                      isIconOnly
                    />
                  </div>
                </div>
                <div className="flex-1 ml-2">
                  <Controller
                    name="error_messages"
                    control={form.control}
                    render={({ field: { value } }) => (
                      <Textarea
                        label={"Message d'Erreur"}
                        labelPlacement="outside"
                        size="sm"
                        radius="full"
                        variant="bordered"
                        isReadOnly
                        disableAutosize
                        classNames={{
                          input: "h-48 max-h-64 overflow-auto !resize-none",
                        }}
                        value={formatErrorMessages(value)}
                      />
                    )}
                  />
                </div>
              </CardBody>
            </form>
          </Form>
        </Card>
        <div className="absolute top-14 right-14 2xl:top-10 2xl:right-10">
          {id && (
            <Button
              href={`${import.meta.env.VITE_BACKEND_URL}/k2/k2_data_upload/reload/${id}`}
              variant="bordered"
              color="primary"
              radius="full"
              onPress={handleReload}
              className="bg-white border-1 border-neutral-300 shadow-xl"
            >
              <ArrowPathIcon className="w-4 h-4 text-primary" />
              Reload
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
